import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Popover, TextField, Tooltip, Typography } from '@mui/material';

/** Chuẩn hóa #rrggbb cho input type=color và hiển thị */
export function normalizeHex(c: string | undefined): string {
  if (!c || typeof c !== 'string') return '#000000';
  let s = c.trim();
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#000000';
}

const PRESET_COLOR_ROWS: string[][] = [
  ['#000000', '#1f2937', '#374151', '#6b7280', '#9ca3af', '#e5e7eb', '#ffffff'],
  ['#450a0a', '#991b1b', '#ea580c', '#f59e0b', '#fbbf24', '#f472b6', '#ec4899'],
  ['#701a75', '#86198f', '#a855f7', '#6366f1', '#2563eb', '#0891b2', '#0d9488'],
  ['#064e3b', '#166534', '#22c55e', '#84cc16', '#ca8a04', '#b45309', '#78350f'],
];

const COLOR_PARENT_DEBOUNCE_MS = 140;

export type ColorPickerPopoverProps = {
  value: string;
  onChange: (hex: string) => void;
  label: string;
  tooltip?: string;
};

export function ColorPickerPopover({ value, onChange, label, tooltip }: ColorPickerPopoverProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const nativeInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCommitRef = useRef<string | null>(null);

  const hex = normalizeHex(value);
  const [hexDraft, setHexDraft] = useState(hex);
  /** Màu đang kéo trong picker — chỉ để hiển thị ô tròn, không ép sync parent mỗi frame */
  const [liveSwatchHex, setLiveSwatchHex] = useState<string | null>(null);

  useEffect(() => {
    setHexDraft(hex);
  }, [hex]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const open = Boolean(anchor);
  const swatchDisplayHex = liveSwatchHex ?? hex;

  const flushDebouncedParent = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const v = pendingCommitRef.current;
    if (v != null) {
      onChange(normalizeHex(v));
      pendingCommitRef.current = null;
    }
  }, [onChange]);

  const scheduleParentColor = useCallback(
    (raw: string) => {
      const n = normalizeHex(raw);
      pendingCommitRef.current = n;
      setHexDraft(n);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const pending = pendingCommitRef.current;
        if (pending != null) {
          onChange(normalizeHex(pending));
          pendingCommitRef.current = null;
        }
      }, COLOR_PARENT_DEBOUNCE_MS);
    },
    [onChange],
  );

  const handleClosePopover = useCallback(() => {
    flushDebouncedParent();
    setLiveSwatchHex(null);
    setAnchor(null);
  }, [flushDebouncedParent]);

  const applyHexInput = () => {
    const n = normalizeHex(hexDraft);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingCommitRef.current = null;
    onChange(n);
    setHexDraft(n);
    setLiveSwatchHex(null);
  };

  return (
    <>
      <Tooltip title={tooltip ?? label}>
        <Box
          component="button"
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'divider',
            bgcolor: swatchDisplayHex,
            cursor: 'pointer',
            p: 0,
            flexShrink: 0,
            boxShadow: (theme) =>
              `inset 0 0 0 1px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
          }}
          aria-label={label}
        />
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              mt: 1,
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.2)',
              maxWidth: 320,
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: 288 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
            Màu sắc · {label}
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="#hex hoặc dán mã"
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={applyHexInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyHexInput();
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Màu có sẵn
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {PRESET_COLOR_ROWS.map((row, ri) => (
              <Box key={ri} sx={{ display: 'flex', gap: 0.75, justifyContent: 'space-between' }}>
                {row.map((c) => (
                  <Box
                    key={c}
                    component="button"
                    type="button"
                    title={c}
                    onClick={() => {
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                        debounceTimerRef.current = null;
                      }
                      pendingCommitRef.current = null;
                      setLiveSwatchHex(null);
                      onChange(c);
                      setHexDraft(c);
                      setAnchor(null);
                    }}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: c,
                      border: '2px solid',
                      borderColor: swatchDisplayHex.toLowerCase() === c.toLowerCase() ? 'primary.main' : 'divider',
                      p: 0,
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'transform 0.12s ease',
                      '&:hover': { transform: 'scale(1.08)' },
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => {
              const el = nativeInputRef.current;
              if (el) {
                el.value = hex;
              }
              nativeInputRef.current?.click();
            }}
          >
            Bảng màu hệ thống…
          </Button>
          <input
            ref={nativeInputRef}
            type="color"
            tabIndex={-1}
            aria-hidden
            onInput={(e) => {
              const v = (e.target as HTMLInputElement).value;
              setLiveSwatchHex(v);
              scheduleParentColor(v);
            }}
            onChange={(e) => {
              const v = e.target.value;
              setLiveSwatchHex(v);
              scheduleParentColor(v);
            }}
            style={{
              position: 'fixed',
              opacity: 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Popover>
    </>
  );
}
